import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { enviroment } from "../enviroment";
import { useProfileContext } from "../contexts/ProfileContext";
import { useOrganizationImpersonationContext } from "../contexts/OrganizationImpersonationContext";
import { collectEmails } from "../routing/useIsApiKeyAdmin";
import { getClientSessionId } from "../data/clients/clientCorrelation";
import { buildClarityIdentity } from "../utils/analytics/clarityIdentity";
import { applyIdentity } from "../utils/analytics/clarity";

// AuthenticatedView hook (flag REACT_APP_CLARITY_ENABLED; only mounted by
// ClarityIdentity when the flag is on): builds the Clarity identity from
// the profile/MSAL/act-as state and hands it to the facade. Runs only when
// the identity itself changes; route changes are handled by the RootLayout
// hook via resendIdentity(). 0 data calls - everything is already in the
// contexts. The e-mail is read only to the internal boolean and never
// leaves the browser.
export function useClarityIdentity(): void {
  const { profile, organization, role, organizationId, b2CId } =
    useProfileContext();
  const { organizationId: actAsOrgId } = useOrganizationImpersonationContext();
  const { accounts } = useMsal();
  const { i18n } = useTranslation();
  const account = accounts[0];
  const localAccountId = account?.localAccountId;
  const parentId = organization?.parentId;
  const organizationName =
    profile?.userOrganizationInformations?.organizationName;
  const profileB2CId = profile?.userOrganizationInformations?.b2CId ?? b2CId;
  const language = i18n.language;

  useEffect(() => {
    const identity = buildClarityIdentity({
      role,
      organizationId,
      parentId,
      organizationName,
      b2CId: profileB2CId,
      msalLocalAccountId: localAccountId,
      emails: account ? collectEmails(account) : [],
      internalDomains: enviroment.clarityInternalEmailDomains ?? [],
      actAsActive: enviroment.actAsVendorEnabled && actAsOrgId != null,
      actAsOrgId,
      language: language || "en",
      kmSession: enviroment.clientCorrelationEnabled
        ? getClientSessionId()
        : undefined,
    });
    // Profile not ready yet (act-as switch): nothing to send.
    if (!identity) return;
    applyIdentity(identity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    role,
    organizationId,
    parentId,
    actAsOrgId,
    language,
    localAccountId,
    profileB2CId,
    organizationName,
  ]);
}
